import { useCallback, useEffect, useState } from 'react';
import Button from '../../../components/Button';
import { journeyService } from '../services/journeyService';
import type {
    JourneyChapterTemplateFirestore,
    JourneyItemTemplateFirestore,
    JourneySectionTemplateFirestore,
    JourneyState,
} from '../services/journeyTypes';

interface JourneyProgressManagerProps {
    userId: string;
    chapterTemplates: JourneyChapterTemplateFirestore[];
    onJourneyUpdate: () => void;
}

export const JourneyProgressManager = ({ userId, chapterTemplates, onJourneyUpdate }: JourneyProgressManagerProps) => {
    const [journeyState, setJourneyState] = useState<JourneyState | null>(null);
    const [isLoadingState, setIsLoadingState] = useState(true);

    const [selectedChapterId, setSelectedChapterId] = useState('');
    const [selectedSectionId, setSelectedSectionId] = useState('');
    const [selectedItemId, setSelectedItemId] = useState('');

    const [sections, setSections] = useState<JourneySectionTemplateFirestore[]>([]);
    const [items, setItems] = useState<JourneyItemTemplateFirestore[]>([]);
    const [isLoadingSections, setIsLoadingSections] = useState(false);
    const [isLoadingItems, setIsLoadingItems] = useState(false);

    const [isJumping, setIsJumping] = useState(false);

    const refreshJourneyState = useCallback(async () => {
        if (!userId) return;
        setIsLoadingState(true);
        try {
            const state = await journeyService.getUserJourneyState(userId);
            setJourneyState(state);
        } catch (error) {
            console.error('Failed to load journey state:', error);
        } finally {
            setIsLoadingState(false);
        }
    }, [userId]);

    useEffect(() => {
        refreshJourneyState();
    }, [refreshJourneyState]);

    useEffect(() => {
        if (!selectedChapterId) {
            setSections([]);
            setSelectedSectionId('');
            setItems([]);
            setSelectedItemId('');
            return;
        }

        let cancelled = false;
        const loadSections = async () => {
            setIsLoadingSections(true);
            setSelectedSectionId('');
            setItems([]);
            setSelectedItemId('');
            try {
                const data = await journeyService.getSections(selectedChapterId);
                if (!cancelled) setSections(data);
            } catch (error) {
                console.error('Failed to load sections:', error);
                if (!cancelled) setSections([]);
            } finally {
                if (!cancelled) setIsLoadingSections(false);
            }
        };
        loadSections();
        return () => { cancelled = true; };
    }, [selectedChapterId]);

    useEffect(() => {
        if (!selectedChapterId || !selectedSectionId) {
            setItems([]);
            setSelectedItemId('');
            return;
        }

        let cancelled = false;
        const loadItems = async () => {
            setIsLoadingItems(true);
            setSelectedItemId('');
            try {
                const data = await journeyService.getItems(selectedChapterId, selectedSectionId);
                if (!cancelled) setItems(data);
            } catch (error) {
                console.error('Failed to load items:', error);
                if (!cancelled) setItems([]);
            } finally {
                if (!cancelled) setIsLoadingItems(false);
            }
        };
        loadItems();
        return () => { cancelled = true; };
    }, [selectedChapterId, selectedSectionId]);

    const canJump = selectedChapterId && selectedSectionId && selectedItemId && !isJumping;

    const handleJump = async () => {
        if (!userId || !canJump) return;

        const selectedChapter = chapterTemplates.find((c) => c.id === selectedChapterId);
        const selectedSection = sections.find((s) => s.id === selectedSectionId);
        const selectedItem = items.find((i) => i.id === selectedItemId);

        if (!selectedChapter || !selectedSection || !selectedItem) return;

        const confirmed = window.confirm(
            `Jump to Chapter ${selectedChapter.number} "${selectedChapter.title}" → ` +
            `Section "${selectedSection.header}" → ` +
            `Item "${selectedItem.title}"?\n\n` +
            `Everything before this position will be marked as claimed. ` +
            `The target item will be set to available. Everything after will be locked.`,
        );
        if (!confirmed) return;

        setIsJumping(true);
        try {
            await journeyService.jumpToJourneyPosition(
                userId,
                chapterTemplates,
                selectedChapterId,
                selectedSectionId,
                selectedItemId,
            );
            await refreshJourneyState();
            onJourneyUpdate();
            alert('Journey position updated successfully.');
        } catch (error) {
            console.error('Failed to jump to position:', error);
            alert('Failed to update journey position. Check the console for details.');
        } finally {
            setIsJumping(false);
        }
    };

    const currentChapterLabel = isLoadingState
        ? 'Loading...'
        : journeyState
            ? `Chapter ${journeyState.currentChapterNumber}`
            : 'No journey data';

    const selectClasses = 'max-w-full px-3 py-2 border-r-8 border-transparent outline outline-neutral-700 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500';

    return (
        <div className="mb-6">
            <h2 className="text-xl font-bold mb-4 text-blue-800">
                Journey Progress Manager ({currentChapterLabel})
            </h2>

            <div className="flex flex-wrap gap-2 items-end">
                <div className="flex flex-col gap-1 max-w-full">
                    <label htmlFor="chapter-select" className="text-xs font-medium text-gray-500">
                        Chapter
                    </label>
                    <select
                        id="chapter-select"
                        value={selectedChapterId}
                        onChange={(e) => setSelectedChapterId(e.target.value)}
                        className={selectClasses}
                        disabled={isJumping}
                    >
                        <option value="">Select chapter...</option>
                        {chapterTemplates.map((chapter) => (
                            <option key={chapter.id} value={chapter.id!}>
                                Ch {chapter.number}: {chapter.title}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="flex flex-col gap-1 max-w-full">
                    <label htmlFor="section-select" className="text-xs font-medium text-gray-500">
                        Section
                    </label>
                    <select
                        id="section-select"
                        value={selectedSectionId}
                        onChange={(e) => setSelectedSectionId(e.target.value)}
                        className={selectClasses}
                        disabled={!selectedChapterId || isLoadingSections || isJumping}
                    >
                        <option value="">
                            {isLoadingSections ? 'Loading...' : 'Select section...'}
                        </option>
                        {sections.map((section) => (
                            <option key={section.id} value={section.id!}>
                                {section.order}. {section.header}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="flex flex-col gap-1 max-w-full">
                    <label htmlFor="item-select" className="text-xs font-medium text-gray-500">
                        Item
                    </label>
                    <select
                        id="item-select"
                        value={selectedItemId}
                        onChange={(e) => setSelectedItemId(e.target.value)}
                        className={selectClasses}
                        disabled={!selectedSectionId || isLoadingItems || isJumping}
                    >
                        <option value="">
                            {isLoadingItems ? 'Loading...' : 'Select item...'}
                        </option>
                        {items.map((item) => (
                            <option key={item.id} value={item.id!}>
                                {item.order}. {item.title} ({item.type})
                            </option>
                        ))}
                    </select>
                </div>

                <Button
                    onClick={handleJump}
                    variant="primary"
                    disabled={!canJump}
                    loading={isJumping}
                >
                    {isJumping ? 'Jumping...' : 'Jump to Position'}
                </Button>
            </div>

            <div className="mt-3 text-sm text-gray-600">
                <p><strong>How it works:</strong></p>
                <ul className="list-disc list-inside space-y-1 mt-1">
                    <li>All chapters/sections/items <strong>before</strong> the target are marked as claimed/completed.</li>
                    <li>The selected item is set to <strong>available</strong>.</li>
                    <li>Everything <strong>after</strong> the target is locked.</li>
                    <li>Chapters up to the target are compiled automatically if not already.</li>
                </ul>
            </div>
        </div>
    );
};
